import { Badge } from '@/react/components/Badge';
import { localizeDate } from '@/react/common/date-utils';
import { useRegistry } from '@/react/portainer/registries/queries/useRegistry';
import { useStack } from '@/react/common/stacks/queries/useStack';
import { GitReferenceCard } from '@/react/portainer/gitops/GitReferenceCard';

import { Alert } from '@@/Alert';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { Card } from '@@/primitives/Card';

import { HelmRelease } from '../types';
import { RegistryIcon } from '../components/RegistryIcon';

import {
  DeploymentStatus,
  getStatusColor,
  getStatusText,
} from './helm-status-utils';
import { useGitChartInfoFromStack } from './useGitChartInfoFromStack';
import { SyncWithGitButton } from './SyncWithGitButton';

interface Props {
  release: HelmRelease;
  latestRevision?: number;
  selectedRevision: number;
}

export function HelmSummary({
  release,
  latestRevision,
  selectedRevision,
}: Props) {
  const stackQuery = useStack(release.stackID);
  let { HelmChartPath: chartPath } = stackQuery?.data || {};
  if (chartPath === './') {
    chartPath = undefined;
  }

  // To compare with git, we need to be able to fetch chart info from the git repository
  const gitChartInfoQuery = useGitChartInfoFromStack(stackQuery.data);
  // check if the latest chart version from git matches the deployed chart version
  const isGitChartVersionOutOfSync =
    gitChartInfoQuery.isSuccess &&
    release.chart?.metadata?.version !== gitChartInfoQuery.data?.version &&
    // only show the out of sync badge if the selected version is the latest version available
    selectedRevision === latestRevision;
  const showGitOption = gitChartInfoQuery.isSuccess;

  const isSuccess =
    release.info?.status === DeploymentStatus.DEPLOYED ||
    release.info?.status === DeploymentStatus.SUPERSEDED;

  return (
    <div>
      <div className="mt-4 flex flex-col gap-y-4">
        <div>
          <Badge type={getStatusColor(release.info?.status)}>
            {getStatusText(release.info?.status)}
          </Badge>
        </div>
        {!!release.info?.description && !isSuccess && (
          <Alert color={getAlertColor(release.info?.status)}>
            {release.info?.description}
          </Alert>
        )}
        <Card.Container variant="filled">
          <Card.Body>
            <div className="form-section-title">Details</div>
            <div
              className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm lg:grid-cols-2 xl:grid-cols-3"
              data-cy="helm-release-info"
            >
              {!!release.namespace && (
                <div className="min-w-0">
                  <span className="text-muted">Namespace: </span>
                  <span data-cy="helm-info-namespace">{release.namespace}</span>
                </div>
              )}
              {!!release.version && (
                <div className="min-w-0">
                  <span className="text-muted">Revision: </span>
                  <span data-cy="helm-info-revision">#{release.version}</span>
                </div>
              )}
              {!!release.chart?.metadata?.name && (
                <div className="min-w-0">
                  <span className="text-muted">Chart: </span>
                  <span data-cy="helm-info-chart">
                    {chartPath || release.chart.metadata.name}
                  </span>
                </div>
              )}
              <ChartReferenceBadge chartReference={release.chartReference} />
              {!!release.chart?.metadata?.appVersion && (
                <div className="min-w-0">
                  <span className="text-muted">App version: </span>
                  <span data-cy="helm-info-app-version">
                    {release.chart.metadata.appVersion}
                  </span>
                </div>
              )}
              {!!release.chart?.metadata?.version && (
                <div className="min-w-0">
                  <span className="text-muted">Chart version: </span>
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <span data-cy="helm-info-chart-version">
                      {release.chart.metadata.name}-
                      {release.chart.metadata.version}
                    </span>
                    {showGitOption &&
                      isGitChartVersionOutOfSync &&
                      release.stackID && (
                        <TooltipWithChildren
                          message={`New chart version available: ${gitChartInfoQuery.data?.version}`}
                          position="top"
                        >
                          <span>
                            <SyncWithGitButton
                              stackID={release.stackID}
                              release={release}
                              dataCy="chart-sync-with-git"
                            />
                          </span>
                        </TooltipWithChildren>
                      )}
                  </span>
                </div>
              )}
              {!!release.info?.last_deployed && (
                <div className="min-w-0">
                  <span className="text-muted">Last deployed: </span>
                  <span data-cy="helm-info-last-deployed">
                    {localizeDate(new Date(release.info.last_deployed))}
                  </span>
                </div>
              )}
            </div>
          </Card.Body>
        </Card.Container>
        {!!stackQuery.data?.GitConfig && !!stackQuery.data?.GitSourceId && (
          <GitReferenceCard
            stackType="helm"
            autoUpdate={stackQuery.data.AutoUpdate}
            gitConfig={stackQuery.data.GitConfig}
            sourceId={stackQuery.data.GitSourceId}
          />
        )}
      </div>
    </div>
  );
}

function ChartReferenceBadge({
  chartReference,
}: {
  chartReference: HelmRelease['chartReference'];
}) {
  const isOCI = !!chartReference?.registryID;
  const registryQuery = useRegistry(chartReference?.registryID);

  const hasNoReference =
    !chartReference?.repoURL && !chartReference?.registryID;
  if (hasNoReference) {
    return null;
  }

  // If it's not OCI (has repoURL), show the repository URL directly
  if (!isOCI) {
    if (chartReference?.repoURL) {
      return (
        <div className="min-w-0">
          <span className="text-muted">Chart source: </span>
          <span>{chartReference.repoURL}</span>
        </div>
      );
    }

    return null;
  }

  // For OCI registries, we need the registry data
  if (!registryQuery.data) {
    return null;
  }

  const registryName = registryQuery.data.Name;
  const registryType = registryQuery.data.Type;
  const registryURL = registryQuery.data.URL;

  return (
    <div className="min-w-0">
      <span className="text-muted">Chart source: </span>
      <TooltipWithChildren message={registryURL} position="top">
        <span className="inline-flex items-center gap-1">
          <RegistryIcon
            type={registryType}
            size="md"
            className="!h-4 !w-auto"
          />
          {registryName}
        </span>
      </TooltipWithChildren>
    </div>
  );
}

function getAlertColor(status?: string) {
  switch (status?.toLowerCase()) {
    case DeploymentStatus.DEPLOYED:
      return 'success';
    case DeploymentStatus.FAILED:
      return 'error';
    case DeploymentStatus.PENDING:
    case DeploymentStatus.PENDINGUPGRADE:
    case DeploymentStatus.PENDINGROLLBACK:
    case DeploymentStatus.UNINSTALLING:
      return 'warn';
    case DeploymentStatus.SUPERSEDED:
    case undefined:
    default:
      return 'info';
  }
}
